import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api_upsertMyProfile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

const FRUSTRATIONS = [
  "Miss too many greens",
  "3-putts",
  "Inconsistent contact",
  "Bad wedge distance control",
  "Tee shots off-line",
  "Poor short game around greens",
];

const GOALS = [
  "Break 100",
  "Break 90",
  "Break 80",
  "Break 70",
  "Improve short game",
  "Hit more greens in regulation",
  "Play more consistent rounds",
  "Hit more fairways off the tee",
];

const TIMES = [15, 30, 45, 60, 90, 120];
const LOCATIONS = [
  { id: "range", label: "Driving range" },
  { id: "short_game", label: "Short game area" },
  { id: "putting", label: "Putting green" },
  { id: "home", label: "Home / net" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || "",
    handicap: 15,
    frustrations: [],
    goals: [],
    default_time_minutes: 30,
    locations: ["range"],
  });

  const toggleArr = (key, value) => {
    setForm((f) => {
      const cur = f[key];
      return { ...f, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  };

  const canNext = () => {
    if (step === 0) return form.name.trim().length > 0;
    if (step === 1) return typeof form.handicap === "number" && form.handicap >= -2 && form.handicap <= 30;
    if (step === 2) return form.frustrations.length > 0;
    if (step === 3) return form.goals.length > 0;
    if (step === 4) return form.locations.length > 0;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const profile = await api_upsertMyProfile(form);
      await refresh();
      toast.success(`Welcome, ${profile.name}!`);
      navigate("/home");
    } catch (e) {
      toast.error("Could not save profile. Please try again.");
      setLoading(false);
    }
  };

  const steps = [
    { key: "name", label: "Who are you" },
    { key: "handicap", label: "Handicap" },
    { key: "frustrations", label: "Leaks" },
    { key: "goals", label: "Goals" },
    { key: "time", label: "Preferences" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Logo size={36} />
          <span className="font-heading font-black tracking-tight text-lg">Fairway Lab</span>
        </div>
        <button data-testid="onboarding-skip" onClick={() => navigate("/")} className="text-sm font-semibold text-slate-500 hover:text-slate-900">
          Cancel
        </button>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {steps.map((s, i) => (
            <div key={s.key} className="flex-1 flex items-center gap-2">
              <div
                className={`h-1.5 rounded-full flex-1 transition-all ${i <= step ? "bg-[#0B2545]" : "bg-slate-200"}`}
                data-testid={`progress-${i}`}
              />
            </div>
          ))}
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Step {step + 1} of {steps.length}</p>

        {step === 0 && (
          <div className="mt-3">
            <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">First, what should we call you?</h2>
            <p className="mt-3 text-slate-600">A name helps us personalize your practice identity.</p>
            <input
              data-testid="onboarding-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Alex"
              className="mt-8 w-full rounded-xl border border-slate-300 bg-white px-5 py-4 text-lg font-semibold focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/20 outline-none"
              autoFocus
            />
          </div>
        )}

        {step === 1 && (
          <div className="mt-3">
            <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">What's your handicap?</h2>
            <p className="mt-3 text-slate-600">If you don't have one, use your typical score minus 72. Plus handicaps welcome.</p>
            <div className="mt-10 flex items-end gap-6">
              <div className="metric text-6xl sm:text-7xl font-heading font-black tabular-nums" data-testid="onboarding-handicap-display">
                {form.handicap < 0 ? `+${Math.abs(form.handicap)}` : form.handicap}
              </div>
              <span className="text-slate-500 font-semibold mb-2">handicap</span>
            </div>
            <input
              data-testid="onboarding-handicap"
              type="range"
              min={-2}
              max={30}
              value={form.handicap}
              onChange={(e) => setForm({ ...form, handicap: parseInt(e.target.value, 10) })}
              className="mt-6 w-full accent-[#0B2545]"
            />
            <div className="flex justify-between text-xs font-bold text-slate-400 mt-1">
              <span>low (+2 to 5)</span><span>mid (10–19)</span><span>high (20–30)</span>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-3">
            <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">What frustrates you most?</h2>
            <p className="mt-3 text-slate-600">Pick all that apply. We'll prioritize these in your sessions.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FRUSTRATIONS.map((f) => {
                const active = form.frustrations.includes(f);
                return (
                  <button
                    key={f}
                    data-testid={`frustration-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleArr("frustrations", f)}
                    className={`text-left p-4 rounded-2xl border-2 transition-all active:scale-[0.98] ${active ? "border-[#0B2545] bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{f}</span>
                      {active && <Check className="w-5 h-5 text-[#0B2545]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-3">
            <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">What are your goals?</h2>
            <p className="mt-3 text-slate-600">Pick one or more concrete targets.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {GOALS.map((g) => {
                const active = form.goals.includes(g);
                return (
                  <button
                    key={g}
                    data-testid={`goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleArr("goals", g)}
                    className={`text-left p-4 rounded-2xl border-2 transition-all active:scale-[0.98] ${active ? "border-[#0B2545] bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{g}</span>
                      {active && <Check className="w-5 h-5 text-[#0B2545]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-3">
            <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">How much time do you usually have?</h2>
            <p className="mt-3 text-slate-600">You can always change this later.</p>

            <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Default session</p>
            <div className="mt-3 flex gap-2 flex-wrap">
              {TIMES.map((t) => {
                const active = form.default_time_minutes === t;
                return (
                  <button
                    key={t}
                    data-testid={`time-${t}`}
                    onClick={() => setForm({ ...form, default_time_minutes: t })}
                    className={`px-5 py-3 rounded-full font-bold transition-all active:scale-95 ${active ? "bg-[#0B2545] text-white" : "bg-slate-100 text-slate-900 hover:bg-slate-200"}`}
                  >
                    {t} min
                  </button>
                );
              })}
            </div>

            <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Preferred locations <span className="font-semibold normal-case tracking-normal text-slate-400">(pick one or more)</span></p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {LOCATIONS.map((l) => {
                const active = form.locations.includes(l.id);
                return (
                  <button
                    key={l.id}
                    data-testid={`location-${l.id}`}
                    onClick={() => toggleArr("locations", l.id)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${active ? "border-[#0B2545] bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{l.label}</span>
                      {active && <Check className="w-5 h-5 text-[#0B2545]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="mt-12 flex items-center justify-between pb-32">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="onboarding-back"
            disabled={step === 0}
            className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < steps.length - 1 ? (
            <button
              onClick={() => canNext() && setStep((s) => s + 1)}
              data-testid="onboarding-next"
              disabled={!canNext()}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              data-testid="onboarding-submit"
              disabled={loading}
              className="btn-primary disabled:opacity-60"
            >
              {loading ? "Building your identity..." : "Enter the lab"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
